import type RealtimeKitClient from "@cloudflare/realtimekit";

type SelfWithSpeakerApi = RealtimeKitClient["self"] & {
    setupSpeaker?: () => Promise<void>;
    getSpeakerDevices?: () => Promise<MediaDeviceInfo[]>;
    getAudioDevices?: () => Promise<MediaDeviceInfo[]>;
    setSpeakerDevice?: (
        device: MediaDeviceInfo,
        opts: { saveDevicePreference: boolean },
    ) => Promise<void>;
    setDevice?: (device: MediaDeviceInfo) => Promise<void>;
    getCurrentDevices?: () => {
        audio?: MediaDeviceInfo;
        video?: MediaDeviceInfo;
        speaker?: MediaDeviceInfo;
    };
};

type MeetingWithAudioPlayback = RealtimeKitClient & {
    audio?: {
        playTracks?: (tracks: MediaStreamTrack[]) => Promise<void>;
        play?: () => Promise<void>;
        setSpeakerDevice?: (deviceId: string) => void;
    };
};

type AudioSessionNavigator = Navigator & {
    audioSession?: { type?: string };
};

export type MobileSpeakerOptions = {
    /**
     * Só com gesto do usuário (botão "Ouvir alto").
     * Faz kick de mic + Speakerphone Android + troca agressiva de sessão.
     * NUNCA usar em intervalo / visibility / participantJoined — isso pode
     * piorar quedas de conexão.
     */
    routeKick?: boolean;
    /** @deprecated Use `routeKick`. Mantido por compatibilidade. */
    iosMicPulse?: boolean;
    /**
     * Observador / só-ouvindo: força sessão `playback` (alto-falante no iOS),
     * mesmo se o SDK reportar estado ambíguo do mic.
     */
    listenOnly?: boolean;
    root?: ParentNode | null;
};

export type SpeakerBoostResult = {
    ok: boolean;
    mediaElements: number;
    played: number;
    audioSessionSet: boolean;
    speakerDeviceSet: boolean;
    sinkIdApplied: number;
    iosPulse: boolean;
    mirroredTracks: number;
    androidSpeakerMic: boolean;
    /** Mic/saída resetados para "padrão" (corrige fone fantasma). */
    defaultDeviceReset: boolean;
    message: string;
};

const MIRROR_HOST_ID = "cb-mobile-audio-mirror";

/** Limpa o mirror global (usar no soft reconnect para não ficar com tracks mortas). */
export function clearMobileAudioMirror(): void {
    if (typeof document === "undefined") {
        return;
    }
    const host = document.getElementById(MIRROR_HOST_ID);
    if (!host) {
        return;
    }
    Array.from(host.querySelectorAll("audio")).forEach((el) => {
        const audio = el as HTMLAudioElement;
        try {
            audio.pause();
        } catch {
            // ignore
        }
        audio.srcObject = null;
        audio.remove();
    });
}

/**
 * Restaura áudio mobile com retries (caso típico: voltar do 2º plano / reconectar).
 * Sem routeKick por padrão — seguro para estabilidade.
 */
export function scheduleMobileAudioRestore(
    meetingClient: RealtimeKitClient | null,
    options: MobileSpeakerOptions & { delaysMs?: number[] } = {},
): () => void {
    if (!meetingClient || typeof window === "undefined") {
        return () => undefined;
    }

    const { delaysMs, ...speakerOptions } = options;
    const delays = delaysMs ?? [0, 400, 1200, 2800];
    const timers = delays.map((delay) =>
        window.setTimeout(() => {
            void ensureMobileSpeakerOutput(meetingClient, speakerOptions);
            boostRemoteMediaElements(speakerOptions.root);
        }, delay),
    );

    return () => {
        timers.forEach((id) => window.clearTimeout(id));
    };
}

/**
 * RTK renderiza `<audio>`/`<video>` dentro de Shadow DOM.
 * querySelectorAll no light DOM NÃO enxerga esses elementos — bug do boost antigo.
 */
export function collectMediaElementsDeep(root: ParentNode | null): HTMLMediaElement[] {
    if (!root) {
        return [];
    }

    const results: HTMLMediaElement[] = [];
    const seen = new Set<HTMLMediaElement>();

    const visit = (node: ParentNode) => {
        const list =
            typeof (node as ParentNode & { querySelectorAll?: typeof document.querySelectorAll })
                .querySelectorAll === "function"
                ? Array.from(
                      (node as Document | Element | ShadowRoot).querySelectorAll(
                          "audio, video",
                      ),
                  )
                : [];

        for (const el of list) {
            const media = el as HTMLMediaElement;
            if (!seen.has(media)) {
                seen.add(media);
                results.push(media);
            }
        }

        const all =
            typeof (node as ParentNode & { querySelectorAll?: typeof document.querySelectorAll })
                .querySelectorAll === "function"
                ? Array.from((node as Document | Element | ShadowRoot).querySelectorAll("*"))
                : [];

        for (const el of all) {
            if (el.shadowRoot) {
                visit(el.shadowRoot);
            }
        }
    };

    visit(root);
    return results;
}

function resolveBoostRoot(root?: ParentNode | null): ParentNode | null {
    if (typeof document === "undefined") {
        return null;
    }
    if (root instanceof HTMLElement || root instanceof Document || root instanceof ShadowRoot) {
        return root;
    }
    return (
        document.querySelector(".meeting-room-shell") ??
        document.querySelector("rtk-meeting") ??
        document.body
    );
}

function detectMobileOs(): "ios" | "android" | "other" {
    if (typeof navigator === "undefined") {
        return "other";
    }
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) {
        return "ios";
    }
    if (/Android/i.test(ua)) {
        return "android";
    }
    return "other";
}

/**
 * Sessão de áudio mobile (best practice LiveKit/Stream 2025–2026):
 * - Mic OFF / observador → `playback` (tende a alto-falante no iOS)
 * - Mic ON → `play-and-record` (necessário para WebRTC bidirecional)
 * - `aggressiveKick` só no toque do usuário (nunca em intervalo)
 *
 * Ref MDN: play-and-record pode rotear para o auricular.
 * Ref LiveKit RN #322: playback até precisar do mic.
 */
export function applyMobileAudioSession(options?: {
    preferLoudspeaker?: boolean;
    micEnabled?: boolean;
    /** Só no botão Ouvir alto — alterna tipos para forçar re-rota. */
    aggressiveKick?: boolean;
}): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }
    const session = (navigator as AudioSessionNavigator).audioSession;
    if (!session || typeof session !== "object") {
        return false;
    }
    try {
        const micEnabled = options?.micEnabled === true;
        const aggressiveKick = options?.aggressiveKick === true;

        // Só-ouvindo: sempre playback (melhor chance de alto-falante).
        if (!micEnabled) {
            session.type = "playback";
            return true;
        }

        // Falando: play-and-record. Kick agressivo só sob gesto do usuário.
        if (aggressiveKick) {
            session.type = "auto";
            session.type = "play-and-record";
            return true;
        }

        session.type = "play-and-record";
        return true;
    } catch {
        return false;
    }
}

async function applySinkIdIfPossible(
    media: HTMLMediaElement,
    deviceId?: string,
): Promise<boolean> {
    const anyMedia = media as HTMLMediaElement & {
        setSinkId?: (id: string) => Promise<void>;
        sinkId?: string;
    };
    if (typeof anyMedia.setSinkId !== "function") {
        return false;
    }
    const candidates = deviceId
        ? [deviceId, "", "default"]
        : ["", "default"];
    for (const id of candidates) {
        try {
            await anyMedia.setSinkId(id);
            return true;
        } catch {
            // tenta próximo
        }
    }
    return false;
}

/**
 * Garante volume máximo e reprodução ativa nos elementos de mídia da sala
 * (incluindo Shadow DOM do RealtimeKit).
 */
export function boostRemoteMediaElements(root?: ParentNode | null): number {
    const scope = resolveBoostRoot(root);
    const mediaElements = collectMediaElementsDeep(scope);
    let played = 0;

    for (const media of mediaElements) {
        media.volume = 1;
        media.muted = false;
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        if (media.paused) {
            void media
                .play()
                .then(() => {
                    played += 1;
                })
                .catch(() => undefined);
        } else {
            played += 1;
        }
        void applySinkIdIfPossible(media);
    }

    return mediaElements.length;
}

/** Dispositivo que parece fone/BT — no Android/Chrome costuma ser “fantasma”. */
function looksLikeHeadsetOrExternal(label: string, deviceId: string): boolean {
    const text = `${label} ${deviceId}`.toLowerCase();
    return /headset|headphone|fone|bluetooth|airpods|ear.?bud|hands.?free|usb.?audio|earpiece|auricular|communications/.test(
        text,
    );
}

/** Dispositivo “padrão” / embutido — o que a cliente corrigiu manualmente nas configs. */
function looksLikeDefaultOrBuiltin(label: string, deviceId: string): boolean {
    if (deviceId === "default") {
        return true;
    }
    const text = `${label}`.toLowerCase();
    return /default|padr[aã]o|built-?in|interno|microphone|microfone|phone mic/.test(text);
}

function pickDefaultAudioInput(inputs: MediaDeviceInfo[]): MediaDeviceInfo | null {
    if (!inputs.length) {
        return null;
    }

    return (
        inputs.find((d) => d.deviceId === "default") ??
        inputs.find((d) => looksLikeDefaultOrBuiltin(d.label, d.deviceId)) ??
        // Android: Speakerphone como input força saída no alto-falante (OpenVidu).
        inputs.find((d) => /speakerphone/i.test(d.label)) ??
        inputs.find((d) => !looksLikeHeadsetOrExternal(d.label, d.deviceId)) ??
        inputs[0]
    );
}

function pickSpeakerDevice(speakers: MediaDeviceInfo[]): MediaDeviceInfo | null {
    if (!speakers.length) {
        return null;
    }

    // Preferir default/padrão antes de labels genéricos — evita “fone fantasma”.
    return (
        speakers.find((d) => d.deviceId === "default") ??
        speakers.find((d) => /padr[aã]o|default/i.test(d.label)) ??
        speakers.find((d) => /speaker|alto.?falante|loud/i.test(d.label)) ??
        speakers.find((d) => !looksLikeHeadsetOrExternal(d.label, d.deviceId)) ??
        speakers[0]
    );
}

/**
 * Relato real: "Ouvir alto" não resolveu; só melhorou ao escolher mic **padrão**
 * nas configurações. O SO/SDK às vezes fica preso em headset/BT fantasma.
 *
 * Estratégia:
 * 1) Se o mic atual parece fone/BT → resetar para default/padrão
 * 2) Sempre preferir saída default/alto-falante
 * 3) Android: Speakerphone só como fallback se não houver "default"
 */
async function resetToPreferredAudioDevices(
    self: SelfWithSpeakerApi,
    options: { force?: boolean; mobileOs?: "ios" | "android" | "other" } = {},
): Promise<{ micReset: boolean; speakerReset: boolean; usedSpeakerphone: boolean }> {
    const result = {
        micReset: false,
        speakerReset: false,
        usedSpeakerphone: false,
    };

    try {
        const current = self.getCurrentDevices?.();
        const currentMic = current?.audio;

        if (typeof self.getAudioDevices === "function" && typeof self.setDevice === "function") {
            const inputs = await self.getAudioDevices();
            const preferred = pickDefaultAudioInput(inputs);
            const stuckOnHeadset =
                currentMic &&
                looksLikeHeadsetOrExternal(currentMic.label || "", currentMic.deviceId || "");

            const shouldSwitchMic =
                Boolean(preferred) &&
                (options.force === true ||
                    stuckOnHeadset ||
                    (preferred != null &&
                        currentMic?.deviceId != null &&
                        preferred.deviceId !== currentMic.deviceId &&
                        looksLikeDefaultOrBuiltin(preferred.label, preferred.deviceId)));

            if (preferred && shouldSwitchMic && preferred.deviceId !== currentMic?.deviceId) {
                await self.setDevice(preferred);
                result.micReset = true;
                result.usedSpeakerphone = /speakerphone/i.test(preferred.label);
            } else if (
                options.force &&
                options.mobileOs === "android" &&
                preferred &&
                /speakerphone/i.test(preferred.label) &&
                preferred.deviceId !== currentMic?.deviceId
            ) {
                await self.setDevice(preferred);
                result.micReset = true;
                result.usedSpeakerphone = true;
            }
        }

        if (typeof self.getSpeakerDevices === "function") {
            const speakers = await self.getSpeakerDevices();
            const preferredSpeaker = pickSpeakerDevice(speakers);
            const currentSpeaker = current?.speaker;
            const stuckSpeaker =
                currentSpeaker &&
                looksLikeHeadsetOrExternal(
                    currentSpeaker.label || "",
                    currentSpeaker.deviceId || "",
                );

            if (
                preferredSpeaker &&
                (options.force || stuckSpeaker) &&
                preferredSpeaker.deviceId !== currentSpeaker?.deviceId
            ) {
                if (typeof self.setSpeakerDevice === "function") {
                    await self.setSpeakerDevice(preferredSpeaker, {
                        saveDevicePreference: false,
                    });
                    result.speakerReset = true;
                } else if (typeof self.setDevice === "function") {
                    await self.setDevice(preferredSpeaker);
                    result.speakerReset = true;
                }
            }
        }
    } catch {
        // ignore
    }

    return result;
}

async function applySpeakerDevice(self: SelfWithSpeakerApi): Promise<boolean> {
    if (typeof self.getSpeakerDevices !== "function") {
        return false;
    }

    const speakers = await self.getSpeakerDevices();
    const preferred = pickSpeakerDevice(speakers);
    if (!preferred) {
        return false;
    }

    if (typeof self.setSpeakerDevice === "function") {
        await self.setSpeakerDevice(preferred, { saveDevicePreference: false });
        return true;
    }

    if (typeof self.setDevice === "function") {
        await self.setDevice(preferred);
        return true;
    }

    return false;
}

function collectRemoteAudioTracks(meetingClient: RealtimeKitClient): MediaStreamTrack[] {
    const tracks: MediaStreamTrack[] = [];
    const seen = new Set<string>();

    try {
        const joined = meetingClient.participants?.joined;
        const list =
            typeof joined?.toArray === "function"
                ? joined.toArray()
                : Array.from((joined as unknown as Map<string, { audioTrack?: MediaStreamTrack; audioEnabled?: boolean }>)?.values?.() ?? []);

        for (const peer of list) {
            const track = (peer as { audioTrack?: MediaStreamTrack; rawAudioTrack?: MediaStreamTrack })
                .audioTrack ?? (peer as { rawAudioTrack?: MediaStreamTrack }).rawAudioTrack;
            const enabled = (peer as { audioEnabled?: boolean }).audioEnabled !== false;
            if (!track || track.readyState === "ended" || !enabled) {
                continue;
            }
            if (seen.has(track.id)) {
                continue;
            }
            seen.add(track.id);
            tracks.push(track);
        }
    } catch {
        // ignore
    }

    return tracks;
}

function ensureMirrorHost(): HTMLElement | null {
    if (typeof document === "undefined") {
        return null;
    }
    let host = document.getElementById(MIRROR_HOST_ID);
    if (!host) {
        host = document.createElement("div");
        host.id = MIRROR_HOST_ID;
        host.setAttribute("aria-hidden", "true");
        host.style.cssText =
            "position:fixed;width:1px;height:1px;left:-9999px;top:0;overflow:hidden;pointer-events:none;opacity:0.01;";
        document.body.appendChild(host);
    }
    return host;
}

/**
 * Espelha faixas remotas em `<audio>` no body.
 * No Android Chrome, elementos dedicados costumam sair no alto-falante
 * quando o player dentro do shell oculto fica no auricular.
 */
async function mirrorRemoteAudioToBody(
    meetingClient: RealtimeKitClient,
): Promise<number> {
    const tracks = collectRemoteAudioTracks(meetingClient);
    if (!tracks.length) {
        return 0;
    }

    const meeting = meetingClient as MeetingWithAudioPlayback;
    if (typeof meeting.audio?.playTracks === "function") {
        try {
            await meeting.audio.playTracks(tracks);
            if (typeof meeting.audio.play === "function") {
                await meeting.audio.play();
            }
            return tracks.length;
        } catch {
            // cai no mirror manual
        }
    }

    const host = ensureMirrorHost();
    if (!host) {
        return 0;
    }

    const existing = new Map<string, HTMLAudioElement>();
    for (const el of Array.from(host.querySelectorAll("audio"))) {
        const id = el.dataset.trackId;
        if (id) {
            existing.set(id, el);
        }
    }

    const activeIds = new Set(tracks.map((t) => t.id));
    Array.from(existing.entries()).forEach(([id, el]) => {
        if (!activeIds.has(id)) {
            el.pause();
            el.srcObject = null;
            el.remove();
            existing.delete(id);
        }
    });

    let played = 0;
    for (const track of tracks) {
        let audio = existing.get(track.id);
        if (!audio) {
            audio = document.createElement("audio");
            audio.dataset.trackId = track.id;
            audio.autoplay = true;
            audio.setAttribute("playsinline", "");
            audio.setAttribute("webkit-playsinline", "true");
            host.appendChild(audio);
        }

        const stream = audio.srcObject instanceof MediaStream ? audio.srcObject : new MediaStream();
        const hasTrack = stream.getTracks().some((t) => t.id === track.id);
        if (!hasTrack) {
            stream.getTracks().forEach((t) => stream.removeTrack(t));
            stream.addTrack(track);
            audio.srcObject = stream;
        }

        audio.muted = false;
        audio.volume = 1;
        try {
            if (audio.paused) {
                await audio.play();
            }
            played += 1;
        } catch {
            // autoplay ainda bloqueado
        }
    }

    return played;
}

/**
 * Pulso de mic: só funciona se o mic puder ser ligado. Observadores costumam
 * estar mutados — nesse caso usamos um getUserMedia curto só para “chutar”
 * o roteamento (e soltamos o track na hora).
 */
/**
 * Kick de rota — SOMENTE após gesto do usuário.
 * Não chamar em intervalo: getUserMedia / toggle de mic pode derrubar a sessão.
 */
async function mobileRouteKick(
    meetingClient: RealtimeKitClient,
    listenOnly: boolean,
): Promise<boolean> {
    const self = meetingClient.self;
    const micEnabled = !listenOnly && Boolean(self?.audioEnabled);
    applyMobileAudioSession({
        preferLoudspeaker: true,
        micEnabled,
        aggressiveKick: micEnabled,
    });

    // Observador / mic off: só a troca para playback já é o kick seguro.
    if (!micEnabled || !self) {
        return true;
    }

    try {
        await self.disableAudio();
        await new Promise((resolve) => window.setTimeout(resolve, 160));
        await self.enableAudio();
        applyMobileAudioSession({
            preferLoudspeaker: true,
            micEnabled: true,
            aggressiveKick: true,
        });
        return true;
    } catch {
        // cai no fallback getUserMedia
    }

    if (!navigator.mediaDevices?.getUserMedia) {
        return false;
    }

    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
        });
        // Mantém o track um instante para o SO reavaliar a rota, depois libera.
        await new Promise((resolve) => window.setTimeout(resolve, 220));
        stream.getTracks().forEach((t) => t.stop());
        applyMobileAudioSession({
            preferLoudspeaker: true,
            micEnabled: Boolean(meetingClient.self?.audioEnabled),
            aggressiveKick: true,
        });
        return true;
    } catch {
        return false;
    }
}

function flashVideoShellVisible(): () => void {
    if (typeof document === "undefined") {
        return () => undefined;
    }
    const shell = document.querySelector(".mobile-meeting-audio-shell") as HTMLElement | null;
    if (!shell) {
        return () => undefined;
    }
    const prevOpacity = shell.style.opacity;
    const prevPointer = shell.style.pointerEvents;
    shell.style.opacity = "0.02";
    shell.style.pointerEvents = "none";
    return () => {
        shell.style.opacity = prevOpacity;
        shell.style.pointerEvents = prevPointer;
    };
}

/**
 * No mobile, iOS/Android tendem a rotear WebRTC para o auricular.
 *
 * Caminho seguro (padrão): sessão playback/play-and-record + mirror + play.
 * Sem toggle de mic — seguro para intervalo / visibility (não piora quedas).
 *
 * Caminho com `routeKick: true`: só no botão "Ouvir alto" (gesto do usuário).
 */
export async function ensureMobileSpeakerOutput(
    meetingClient: RealtimeKitClient | null,
    options: MobileSpeakerOptions = {},
): Promise<SpeakerBoostResult> {
    const empty: SpeakerBoostResult = {
        ok: false,
        mediaElements: 0,
        played: 0,
        audioSessionSet: false,
        speakerDeviceSet: false,
        sinkIdApplied: 0,
        iosPulse: false,
        mirroredTracks: 0,
        androidSpeakerMic: false,
        defaultDeviceReset: false,
        message: "Cliente de reunião indisponível",
    };

    if (!meetingClient) {
        return empty;
    }

    const routeKick = options.routeKick === true || options.iosMicPulse === true;
    const mobileOs = detectMobileOs();
    const self = meetingClient.self as SelfWithSpeakerApi;
    const micActuallyOn = Boolean(meetingClient.self?.audioEnabled);
    // Observador / listenOnly: trata como mic off → playback (best practice).
    const listenOnly = options.listenOnly === true || !micActuallyOn;
    const micEnabled = !listenOnly && micActuallyOn;
    const restoreShell = routeKick ? flashVideoShellVisible() : () => undefined;

    const audioSessionSet = applyMobileAudioSession({
        preferLoudspeaker: true,
        micEnabled,
        aggressiveKick: false,
    });

    let speakerDeviceSet = false;
    let androidSpeakerMic = false;
    let defaultDeviceReset = false;

    try {
        // Relato: mic preso em “fone” fantasma — só melhorou com mic padrão.
        // No toque: força reset. Na manutenção: só se detectar headset/BT fantasma.
        const deviceFix = await resetToPreferredAudioDevices(self, {
            force: routeKick,
            mobileOs,
        });
        defaultDeviceReset = deviceFix.micReset || deviceFix.speakerReset;
        androidSpeakerMic = deviceFix.usedSpeakerphone;

        if (typeof self.setupSpeaker === "function") {
            await self.setupSpeaker();
            speakerDeviceSet = true;
        } else {
            speakerDeviceSet = await applySpeakerDevice(self);
        }
    } catch {
        try {
            speakerDeviceSet = await applySpeakerDevice(self);
        } catch {
            speakerDeviceSet = false;
        }
    }

    const speakerId = self.getCurrentDevices?.()?.speaker?.deviceId;

    const scope = resolveBoostRoot(options.root);
    const mediaElements = collectMediaElementsDeep(scope);
    const alreadyMirrored =
        typeof document !== "undefined" &&
        Boolean(document.getElementById(MIRROR_HOST_ID)?.querySelector("audio"));

    let played = 0;
    let sinkIdApplied = 0;

    for (const media of mediaElements) {
        media.volume = 1;
        // Se já há mirror tocando, não desarme o mute do shell (evita blip/eco).
        if (!alreadyMirrored) {
            media.muted = false;
        }
        media.setAttribute("playsinline", "");
        media.setAttribute("webkit-playsinline", "true");
        try {
            if (media.paused) {
                await media.play();
            }
            played += 1;
        } catch {
            // autoplay ainda bloqueado
        }
        if (await applySinkIdIfPossible(media, speakerId)) {
            sinkIdApplied += 1;
        }
    }

    let mirroredTracks = 0;
    try {
        mirroredTracks = await mirrorRemoteAudioToBody(meetingClient);
    } catch {
        mirroredTracks = 0;
    }

    let iosPulse = false;
    if (routeKick && (mobileOs === "ios" || mobileOs === "android")) {
        iosPulse = await mobileRouteKick(meetingClient, listenOnly);

        // Depois do kick, reforça mic/saída padrão (caso o SDK tenha remudado).
        const deviceFixAgain = await resetToPreferredAudioDevices(self, {
            force: true,
            mobileOs,
        });
        defaultDeviceReset =
            defaultDeviceReset || deviceFixAgain.micReset || deviceFixAgain.speakerReset;
        androidSpeakerMic = androidSpeakerMic || deviceFixAgain.usedSpeakerphone;

        // Re-play após o kick de rota
        for (const media of collectMediaElementsDeep(scope)) {
            media.volume = 1;
            try {
                if (media.paused) {
                    await media.play();
                    played += 1;
                }
            } catch {
                // ignore
            }
        }

        try {
            const again = await mirrorRemoteAudioToBody(meetingClient);
            mirroredTracks = Math.max(mirroredTracks, again);
        } catch {
            // ignore
        }
    }

    // Se o mirror / experimentalAudioPlayback está tocando, silencia os
    // <audio>/<video> do shell para não dobrar o som (auricular + alto-falante).
    if (mirroredTracks > 0) {
        for (const media of collectMediaElementsDeep(scope)) {
            media.muted = true;
        }
    }

    restoreShell();

    const found = Math.max(mediaElements.length, collectMediaElementsDeep(scope).length);
    // Sucesso real: reproduziu mídia/mirror OU conseguiu um kick/sink/reset de device.
    // NÃO contar só audioSessionSet — setar a propriedade não garante alto-falante.
    const ok =
        mirroredTracks > 0 ||
        played > 0 ||
        sinkIdApplied > 0 ||
        androidSpeakerMic ||
        defaultDeviceReset ||
        iosPulse ||
        speakerDeviceSet;

    let message: string;
    if (defaultDeviceReset) {
        message =
            "Microfone/saída voltaram para o padrão do aparelho (às vezes o sistema ficava “no fone” sem ter fone). Aumente o volume lateral se ainda estiver baixo.";
    } else if (mirroredTracks > 0 || androidSpeakerMic || (played > 0 && (iosPulse || sinkIdApplied > 0))) {
        message =
            "Áudio no alto-falante. Se ainda estiver baixo, aumente o volume lateral do celular (às vezes é o volume de chamada, não o de mídia).";
    } else if (played > 0) {
        message =
            "Reprodução retomada. Se não ouvir, abra a aba Videochamada e toque de novo em Ouvir alto. Sem fone, confirme que o volume do aparelho não está no mudo.";
    } else if (found === 0 && mirroredTracks === 0) {
        message =
            "Não achei o player de áudio ainda. Abra a aba Videochamada e toque de novo em Ouvir alto.";
    } else {
        message =
            "O navegador bloqueou ou reteve o áudio no auricular. Toque de novo em Ouvir alto, aumente o volume e, se puder, teste na aba Videochamada.";
    }

    return {
        ok,
        mediaElements: found,
        played,
        audioSessionSet,
        speakerDeviceSet,
        sinkIdApplied,
        iosPulse,
        mirroredTracks,
        androidSpeakerMic,
        defaultDeviceReset,
        message,
    };
}
